import { useMemo } from 'react'
import type { OfferType } from '@sevenrooms/domain'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { useLocales } from '@sevenrooms/locales'
import { DateOnly } from '@sevenrooms/timepiece'
import { VStack } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { RequestOrPriorityAlertButton } from '../../../components/RequestOrPriorityAlertButton'
import {
  type AvailabilityTimeWithUpSellCost,
  useExperienceValidDates,
  useReservationsRoute,
  useSingleDayAvailability,
  useVenue,
  useWidgetLanguage,
  useWidgetSettings,
} from '../../../hooks'
import { reservationWidgetMessages } from '../../../reservationWidgetMessages'
import { TimeSlots } from './TimeSlots'
import { TimeSlotsSkeleton } from './TimeSlotsSkeleton'

interface NextAvailableDayProps {
  experienceUrlKey: string
  offerType: OfferType
  clientId?: string
  partySize: number
  startDate?: string
  haloTimeIntervalMinutes?: number
  startTime?: string
  onTimeSlotClick?: (slot: AvailabilityTimeWithUpSellCost) => void
  timeSlotsDisplayCount?: number
  onRequestBtnClick?: (requestDate: string, requestPartySize: number) => void
  onPriorityAlertBtnClick?: (requestDate: string, requestPartySize: number) => void
  showTimeslotTime?: boolean
  descriptionType?: 'short' | 'full'
}

export function NextAvailableDay({
  experienceUrlKey,
  offerType,
  onTimeSlotClick,
  onRequestBtnClick,
  onPriorityAlertBtnClick,
  partySize,
  startDate,
  startTime,
  haloTimeIntervalMinutes,
  clientId,
  showTimeslotTime,
  timeSlotsDisplayCount,
  descriptionType,
}: NextAvailableDayProps) {
  const { formatMessage } = useLocales()
  const { selectedLanguage } = useWidgetLanguage()
  const { id: venueId, urlKey: venueUrlKey, startOfDayTime, availabilityCacheDays, venueToday, currencyCode } = useVenue()
  const { defaultSearchTime } = useWidgetSettings()
  const { channel } = useReservationsRoute()
  const today = venueToday || DateOnly.fromDate(new Date()).toIso()
  const isPrivateEventsExperience = offerType === 'PDR'

  const {
    data: { firstAvailableDayForBooking },
    isLoading: isLoadingValidDates,
    isFetching: isFetchingValidDates,
  } = useExperienceValidDates({
    venueUrlKey,
    partySize,
    startDate: today,
    selectedDate: startDate,
    experienceUrlKey,
    availabilityCacheDays,
  })

  const { isFetching: isAvailabilityFetching, data: nextAvailableDayAvailabilityData } = useSingleDayAvailability({
    venueId,
    venue: venueUrlKey,
    startDate: firstAvailableDayForBooking as string,
    partySize,
    startTime: startTime || ReservationWidget.AllTimesOption,
    selectedLangCode: selectedLanguage,
    haloTimeIntervalMinutes:
      haloTimeIntervalMinutes || (ReservationWidget.HaloMinutesOptions[ReservationWidget.HaloMinutesOptions.length - 1] as number),
    channel: channel || ReservationWidget.SevenRoomsWidgetChannel,
    haloSizeInterval: ReservationWidget.WholeDayHaloSizeInterval,
    timeSlot: defaultSearchTime,
    startOfDayTime,
    skipRequests: isLoadingValidDates || !firstAvailableDayForBooking,
    clientId,
    experienceId: experienceUrlKey,
  })

  const requestOrPriorityAlertAvailability = useMemo(() => {
    const requestTimeRange = nextAvailableDayAvailabilityData?.requestTimeRange
    const reservationTimesInHalo = nextAvailableDayAvailabilityData?.reservationTimesInHalo

    if (requestTimeRange?.length && partySize && firstAvailableDayForBooking) {
      return (
        <RequestOrPriorityAlertButton
          isPrivateEvent={isPrivateEventsExperience}
          buttonVariant={reservationTimesInHalo?.length === 0 ? 'primary' : 'secondary'}
          onRequestButtonClick={() => onRequestBtnClick?.(firstAvailableDayForBooking, partySize)}
          onPriorityAlertButtonClick={() => onPriorityAlertBtnClick?.(firstAvailableDayForBooking, partySize)}
          partySize={partySize}
        />
      )
    }
    return null
  }, [
    firstAvailableDayForBooking,
    isPrivateEventsExperience,
    nextAvailableDayAvailabilityData?.requestTimeRange,
    nextAvailableDayAvailabilityData?.reservationTimesInHalo,
    onPriorityAlertBtnClick,
    onRequestBtnClick,
    partySize,
  ])

  return isLoadingValidDates || (isFetchingValidDates && !firstAvailableDayForBooking) ? (
    <TimeSlotsSkeleton showHeader />
  ) : (
    <>
      {firstAvailableDayForBooking &&
      (isAvailabilityFetching ||
        (nextAvailableDayAvailabilityData && nextAvailableDayAvailabilityData.reservationTimesInHalo.length > 0)) ? (
        <VStack spacing="sm">
          <Text textStyle="body1Bold">{formatMessage(reservationWidgetMessages.resWidgetNextAvailableDateLabel)}</Text>
          <TimeSlots
            timeSlotVariant="secondary"
            timeSlotsDisplayCount={timeSlotsDisplayCount}
            filteredTimeslots={nextAvailableDayAvailabilityData?.reservationTimesInHalo}
            startDate={firstAvailableDayForBooking}
            currencyCode={currencyCode}
            isLoading={isAvailabilityFetching}
            showTimeslotTime={showTimeslotTime}
            onTimeSlotClick={onTimeSlotClick}
            requestOrPriorityAlertAvailability={requestOrPriorityAlertAvailability}
            descriptionType={descriptionType}
          />
        </VStack>
      ) : null}
    </>
  )
}
